import React from 'react';
import Select from './ui/Select';
import Button from './ui/Button';
import type { ContractType } from '../types';

interface Props {
  types: ContractType[];
  contractType: string;          // "" = авто-определение
  role: string;
  onChangeType: (t: string) => void;
  onChangeRole: (r: string) => void;
  disabled?: boolean;
  allowAuto?: boolean;
  center?: boolean;
}

export default function ContractTypeSelect({
  types, contractType, role, onChangeType, onChangeRole,
  disabled, allowAuto = true, center = true,
}: Props) {
  const current = types.find((t) => t.id === contractType);
  const align = center ? 'text-center' : '';
  const justify = center ? 'justify-center' : '';

  // When the type is auto-detected we don't yet know the role vocabulary, so we
  // offer two generic sides that the backend maps to the detected profile.
  const GENERIC_SIDES = [
    { id: 'side_a', label: 'Мы — поставщик / подрядчик / исполнитель' },
    { id: 'side_b', label: 'Мы — покупатель / заказчик' },
  ];
  const roleOptions = current
    ? current.roles.map((r) => ({ id: r.id, label: r.label }))
    : GENERIC_SIDES;

  // The chosen side as an index (0 = поставщик/исполнитель, 1 = покупатель/заказчик),
  // so switching the contract type keeps the side instead of resetting to the supplier.
  const sideIndex = (): number => {
    if (role === 'side_a') return 0;
    if (role === 'side_b') return 1;
    const i = current ? current.roles.findIndex((r) => r.id === role) : -1;
    return i >= 0 ? i : 0;
  };

  const handleType = (id: string) => {
    const idx = sideIndex();              // capture the side BEFORE the type changes
    onChangeType(id);
    const t = types.find((x) => x.id === id);
    if (t) {
      onChangeRole(t.roles[idx]?.id || t.default_role);  // same side in the new type
    } else {
      onChangeRole(idx === 1 ? 'side_b' : 'side_a');      // auto-detect: keep generic side
    }
  };

  const typeOptions = [
    ...(allowAuto ? [{ value: '', label: 'Определить автоматически' }] : []),
    ...types.map((t) => ({ value: t.id, label: t.label })),
  ];

  return (
    <div className="space-y-3">
      <div>
        <p className={`text-sm font-medium text-gray-600 mb-2 ${align}`}>Тип договора:</p>
        <div className={`flex ${justify}`}>
          <Select
            value={contractType}
            options={typeOptions}
            onChange={handleType}
            disabled={disabled}
            className={center ? 'w-64' : 'w-full max-w-xs'}
          />
        </div>
      </div>

      <div>
        <p className={`text-sm font-medium text-gray-600 mb-2 ${align}`}>Анализировать с позиции:</p>
        <div className={`flex flex-wrap gap-2 ${justify}`}>
          {roleOptions.map((r) => (
            <Button
              key={r.id}
              size="sm"
              variant={role === r.id ? 'primary' : 'secondary'}
              onClick={() => onChangeRole(r.id)}
              disabled={disabled}
            >
              {r.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
